import { getSingleListingProperty } from "../../../services/krent.service";
import { PropertyListResponse } from "@interfaces/properties.interface";
import { formatCurrency } from "../../../utils/currency";
import { CycleToInterval } from "../../../utils/constants";

import defaultImage from "../../../assets/images/property-card.png";
// import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Modal } from "@shared/controls/Modal";
import { useNavigate } from "react-router-dom";
import { formatListedDate } from "@pages/SeoPages/helper";
import { formatAvailableFrom, rentedOutUntil } from "../../../utils/rentStatus";

import "./PropertyCard.css";

export const PropertyCard: React.FC<PropertyListResponse> = (
  p: PropertyListResponse,
) => {
  const [showTourModal, setShowTourModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const navigate = useNavigate();
  const isRent = p.intent === "long lease";
  const letUntil = rentedOutUntil(p);
  const [totalPackage, setTotalPackage] = useState<number | null>(p["rentPricing"]?.total ?? null);
  useEffect(() => {
    if (!isRent) return;
    if (p["rentPricing"]?.total != null) { setTotalPackage(p["rentPricing"].total); return; }
    const controller = new AbortController();
    setTotalPackage(null);
    getSingleListingProperty(p.id || p.slug || "", controller.signal, 1).then((response: any) => {
      if (!controller.signal.aborted && response?.success !== false) setTotalPackage(response?.data?.rentPricing?.total ?? null);
    }).catch(() => { /* Keep unavailable quotes out of displayed prices. */ });
    return () => controller.abort();
  }, [isRent, p.id, p.slug, p["rentPricing"]]);

  // Combine coverImage and otherImages for the gallery
  const images = p.coverImage
    ? [p.coverImage, ...(p.otherImages || [])]
    : p.otherImages || [];
  // If no images, use default image
  const displayImages = images.length > 0 ? images : [defaultImage];
  const hasMultipleImages = displayImages.length > 1;

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === 0 ? displayImages.length - 1 : prev - 1,
    );
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === displayImages.length - 1 ? 0 : prev + 1,
    );
  };
  return (
    <div className="col position-relative">
      <div
        className=" h-100 position-relative cursor-pointer property-card"
        style={{ overflow: "hidden" }}
        onClick={() =>
          navigate(`/property/${p.slug}`, {
            state: { property: p, id: p.slug },
          })
        }
      >
        <div className="position-relative">
          {letUntil && (
            <div className="let-sash" aria-label="Rented out">
              <span>Rented out</span>
            </div>
          )}
          {p.isTopListing && (
            <div
              className="position-absolute px-3 py-1 text-white fw-bold fs-12 rounded-pill bg-primary"
              style={{
                top: "20px",
                left: "20px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                zIndex: 10,
              }}
            >
              {p.topListingLabel ? p.topListingLabel : "★ Top Listing"}
            </div>
          )}

          {p["instantBooking"] && (
            <div
              className="position-absolute px-2 py-2 rounded-pill d-flex align-items-center gap-1 instant-booking-badge"
              data-tooltip="Confirmed instantly"
              style={{
                top: "20px",
                left: "20px",
                backgroundColor: "#E0F7FA",
                color: "#00796B",
                fontSize: "0.70rem",
                fontWeight: "600",
                zIndex: p.isTopListing ? 11 : 10,
                marginTop: p.isTopListing ? "35px" : "0",
              }}
            >
              <i className="fa-solid text-primary fa-bolt"></i>
              Instant Booking
            </div>
          )}

          <div
            className="gallery-slider-track"
            style={{
              width: `${displayImages.length * 100}%`,
              transform: `translateX(-${currentImageIndex * (100 / displayImages.length)}%)`,
              transition: "transform 0.8s ease-in-out",
            }}
          >
            {displayImages.map((img, index) => (
              <img
                key={index}
                src={img}
                className="card-img-top"
                alt={p.slug}
                title={p.slug}
                style={{ width: `${100 / displayImages.length}%` }}
              />
            ))}
          </div>

          {hasMultipleImages && (
            <>
              <button
                className="gallery-nav-btn gallery-nav-prev"
                onClick={handlePrevImage}
              >
                <i className="fa-solid fa-chevron-left text-white"></i>
              </button>
              <button
                className="gallery-nav-btn gallery-nav-next"
                onClick={handleNextImage}
              >
                <i className="fa-solid fa-chevron-right text-white"></i>
              </button>
              <div className="gallery-indicators">
                {displayImages.map((_, index) => (
                  <span
                    key={index}
                    className={`gallery-dot ${index === currentImageIndex ? "active" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(index);
                    }}
                  />
                ))}
              </div>
            </>
          )}

          <div
            className="position-absolute w-100"
            style={{
              bottom: 0,
              left: 0,
              height: "150px",
              background:
                "linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent)",
              zIndex: 1,
              borderRadius: "20px",
            }}
          />
          <div
            className="position-absolute w-full px-4"
            style={{ bottom: "25px", left: 0, zIndex: 2 }}
          >
            <div className="d-flex justify-between align-items-center">
              <p className="text-muted fs-12 ">
                <span className="text-white fs-18 fw-bolder">
                  {(!isRent || totalPackage != null) && "₦"}
                  {isRent && totalPackage == null ? "View pricing" : formatCurrency(
                    isRent ? Number(totalPackage) : ((p.hasDailyPrice ? p.dailyPrice : p.price) ?? 0),
                  )}{" "}
                </span>
                {isRent ? (
                  <span className="text-white d-block fs-12">Total package</span>
                ) : p.intent !== "sale" && (
                  <span className="text-white">
                    /{CycleToInterval(p.cycle)}
                  </span>
                )}
                {p.intent === "shortlet" &&
                  p.partyPrice &&
                  p.partyPrice !== null && (
                    <span className="text-primary fw-bold">&nbsp;🎉</span>
                  )}
              </p>
              <button className="rounded-10 fw-500 fs-12 px-2 p-1">
                Quick View
              </button>
            </div>
          </div>

          {/* intent */}
          <div
            className="d-flex justify-content-between position-absolute "
            style={{ top: "21px", right: "21px" }}
          >
            {p.intent === "sale" && (
              <span
                className="badge d-inline-flex fs-12 mb-3 px-2 py-2 fw-semibold text-white"
                style={{ background: "#963780" }}
              >
                FOR SALE
              </span>
            )}
            {/* {p.intent === "long lease" && (
              <span
                className="badge d-inline-flex fs-12 mb-3 px-2 py-2 fw-semibold text-white"
                // className='badge d-inline-flex fs-12 mb-3 px-2 py-2 fw-semibold text-success bg-success bg-opacity-10 border border-success border-opacity-10 rounded-pill'
                style={{ background: "#05569F" }}
              >
                FOR RENT
              </span>
            )} */}
            {/* {p.intent === "shortlet" && (
              <span
                className="badge d-inline-flex fs-12 mb-3 px-2 py-2 fw-semibold text-white"
                style={{ background: "#C0190F" }}
              >
                SHORT LET
              </span>
            )} */}
          </div>
        </div>

        <div className="card-body px-0">
          <div style={{ minHeight: "140px" }}>
            <div className="d-flex justify-content-between mb-3 items-center">
              <p className="fw-bolder fs-14">{p.title}</p>
              {p.virtual_tour_url && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setShowTourModal(true);
                  }}
                  className="btn border text-primary border-primary py-0 rounded-2 font-onest fw-bold fs-12 text-white h-max py-1"
                  style={{ whiteSpace: "nowrap" }}
                >
                  3D Tour
                </button>
              )}
            </div>
            <p className="fs-12 text-muted">
              {p.summary.slice(0, 100)}
              {p.summary.length > 100 ? "..." : "."}
            </p>
            <p className="fs-12 text-muted">{p.location.city}</p>
            {letUntil && (
              <p className="fs-12 fw-semibold let-available-from">
                Rented out · available from {formatAvailableFrom(letUntil)}
              </p>
            )}
            {p.referenceNumber && (
              <p className="fs-12 text-muted">Ref : {p.referenceNumber}</p>
            )}

            {p?.reviewStats?.averageRating !== undefined &&
              p?.reviewStats?.averageRating > 0 && (
                <div className="d-flex mt-2">
                  {[...Array(5)].map((_, i) => {
                    const diff = (p?.reviewStats?.averageRating || 0) - i;

                    if (diff >= 1) {
                      return (
                        <i
                          key={i}
                          className="iconly-Star icbo me-1"
                          style={{ color: "#47BBA9" }}
                        ></i>
                      );
                    }

                    if (diff >= 0.5) {
                      return (
                        <span
                          key={i}
                          className="position-relative me-1"
                          style={{ width: "1.1em", display: "inline-block" }}
                        >
                          <i
                            className="iconly-Star icbo position-absolute top-0 start-0"
                            style={{
                              color: "transparent",
                              WebkitTextStroke: "1px #47BBA9",
                            }}
                          ></i>
                          <i
                            className="iconly-Star icbo position-absolute top-0 start-0"
                            style={{
                              color: "#47BBA9",
                              width: "60%",
                              overflow: "hidden",
                              clipPath: "inset(0 30% 0 0)",
                            }}
                          ></i>
                        </span>
                      );
                    }

                    return (
                      <i
                        key={i}
                        className="iconly-Star icbo me-1"
                        style={{
                          color: "transparent",
                          WebkitTextStroke: "1px #47BBA9",
                        }}
                      ></i>
                    );
                  })}
                </div>
              )}
          </div>
          <div className="d-flex gap-3 align-items-center">
            <div className="d-flex flex-column align-items-center">
              <div className="d-flex gap-2 align-items-center">
                <span className="fs-18 fw-bold text-dark-grey">
                  {p.bedrooms}
                </span>
                <i className="fa-regular fa-bed-front text-primary fs-18"></i>
              </div>
              <span className="fs-14 text-light-grey">Bedrooms</span>
            </div>
            <div className="vr" style={{ height: "50px", width: "1px" }}></div>
            <div className="d-flex flex-column align-items-center">
              <div className="d-flex gap-2 align-items-center">
                <span className="fs-18 fw-bold text-dark-grey">
                  {p.bathrooms}
                </span>
                <i className="fa-regular fa-shower text-primary fs-18"></i>
              </div>
              <span className="fs-14 text-light-grey">Bathrooms</span>
            </div>
          </div>
          <div
            className="flex justify-items-center mt-3"
            style={{ fontStyle: "italic" }}
          >
            <p style={{ color: "#7B8794" }} className="mt-0 mb-2 fs-10">
              {p.updatedAt
                ? `Updated ${formatListedDate(p.updatedAt)}, Added ${formatListedDate(p.createdAt)}`
                : `Added ${formatListedDate(p.createdAt)}`}
            </p>
          </div>
        </div>
      </div>

      {showTourModal && (
        <Modal
          key="tour-modal"
          show={showTourModal}
          onClose={() => {
            setShowTourModal(false);
          }}
          size={900}
          position="top"
        >
          <iframe
            title="Virtual Tour"
            src={p.virtual_tour_url}
            width="100%"
            height="700px"
            allowFullScreen
          ></iframe>
        </Modal>
      )}
    </div>
  );
};
